import { type Employee, type InvitationResponse } from '@sivan/shared';
import { useEffect, useId, useRef, useState } from 'react';

// The one-time sign-in link of a new invitation, shown once to the Super Admin who created it:
// copy it (or share it from a phone) and send it privately. It is never stored by the browser.

const expiry = (iso: string): string =>
  new Intl.DateTimeFormat('he-IL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));

/** The invitation's state on an employee's card: pending (until when), expired, revoked. */
export function invitationLabel(invitation: Employee['invitation']): string | null {
  if (invitation == null) return null;
  switch (invitation.state) {
    case 'pending':
      return `הזמנה ממתינה, בתוקף עד ${expiry(invitation.expiresAt)}`;
    case 'expired':
      return 'פג תוקף ההזמנה';
    case 'revoked':
      return 'ההזמנה בוטלה';
    case 'accepted':
      return null;
  }
}

export function InvitationLink({
  invitation,
  onClose,
}: {
  readonly invitation: InvitationResponse;
  readonly onClose: () => void;
}) {
  const { employee, link, expiresAt } = invitation;
  const field = useRef<HTMLInputElement>(null);
  const hintId = useId();
  const [copied, setCopied] = useState<'copied' | 'failed' | null>(null);
  const canShare = typeof navigator.share === 'function';
  useEffect(() => {
    field.current?.select();
  }, []);
  const copy = () => {
    navigator.clipboard.writeText(link).then(
      () => {
        setCopied('copied');
      },
      () => {
        // Without clipboard access the link stays selected in the field, to copy by hand.
        field.current?.select();
        setCopied('failed');
      },
    );
  };
  const share = () => {
    navigator.share({ title: 'הזמנה למערכת סיון ביצוע', text: link }).catch(() => undefined);
  };
  const noRole =
    employee.organizationRoles.length === 0 &&
    employee.memberships.every((m) => m.roles.length === 0);
  return (
    <div className="invitation">
      <p>
        שלחו את הקישור <strong>בהודעה פרטית</strong> אל {employee.fullName} בלבד. הוא תקף לשימוש
        אחד, עד {expiry(expiresAt)}.
      </p>
      <label className="field">
        <span>קישור ההזמנה</span>
        <input
          ref={field}
          className="invitation__link"
          dir="ltr"
          readOnly
          value={link}
          aria-describedby={hintId}
          onFocus={(event) => {
            event.currentTarget.select();
          }}
        />
      </label>
      <div className="form-actions">
        <button type="button" className="button button--primary" onClick={copy}>
          העתקת הקישור
        </button>
        {canShare && (
          <button type="button" className="button" onClick={share}>
            שיתוף
          </button>
        )}
        <button type="button" className="button" onClick={onClose}>
          סיום
        </button>
      </div>
      {copied === 'copied' && (
        <p className="notice notice--success" role="status">
          הקישור הועתק. הדביקו אותו בהודעה פרטית.
        </p>
      )}
      {copied === 'failed' && (
        <p className="notice notice--warning" role="status">
          לא ניתן להעתיק אוטומטית. הקישור מסומן: העתיקו אותו ידנית.
        </p>
      )}
      <ul className="invitation__steps" id={hintId}>
        <li>
          בפתיחת הקישור, {employee.fullName} יקליד את כתובת העבודה{' '}
          <bdi dir="ltr" className="nowrap">
            {employee.workEmail}
          </bdi>
          , יבחר סיסמה, ובמקרה של מנהל־על יגדיר אפליקציית אימות.
        </li>
        <li>הקישור מוצג כאן פעם אחת בלבד. אם לא נשלח, צרו קישור חדש: הקודם יבוטל.</li>
        {noRole && (
          <li>
            לעובד עדיין אין תפקיד: אחרי הכניסה הוא לא יראה נתונים עד שיוקצה לו תפקיד ארגוני או תפקיד
            בפרויקט.
          </li>
        )}
      </ul>
    </div>
  );
}
